import { XCircleIcon } from '@heroicons/react/24/solid'; // Importing the hero icon
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface PopupProps {
  isOpen?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  message?: string;
  title?: string | React.ReactNode;
  children?: React.ReactNode;
  additionalClass?: string;
  width?: string;
  maxWidth?: string;
  height?: string;
  maxHeight?: string;
  disableCloseButton?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  loading?: boolean;
}

function Popup({
  isOpen = false,
  onClose,
  onConfirm,
  message = '',
  title = 'Déclarations Juridiques',
  children,
  additionalClass = '',
  width = 'fit-content',
  maxWidth = '90vw',
  height = 'fit-content',
  maxHeight = '90vh',
  disableCloseButton = false,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  onCancel,
  loading = false,
}: PopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mainScrollable = document.getElementById('mainScrollable');

    if (isOpen && mainScrollable) {
      // Disable scrolling on the mainScrollable element when the popup is open
      mainScrollable.style.overflow = 'hidden';
      mainScrollable.style.touchAction = 'none';
    } else if (mainScrollable) {
      // Enable scrolling on the mainScrollable element when the popup is closed
      mainScrollable.style.overflow = 'auto';
      mainScrollable.style.touchAction = 'auto';
    }

    return () => {
      // Cleanup when the component is unmounted or when isOpen changes
      if (mainScrollable) {
        mainScrollable.style.overflow = 'auto';
        mainScrollable.style.touchAction = 'auto';
      }
    };
  }, [isOpen]);

  // Close on Escape key for accessibility
  useEffect(() => {
    if (!isOpen) { return; }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {return null;}

  const stopPropagationIfOverlaySurface = (event: React.SyntheticEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      event.stopPropagation();
    }
  };

  const modal = (
    <div
      className="pointer-events-auto fixed inset-0 !m-0 flex items-center justify-center bg-black/50"
      style={{ zIndex: 2147483647, touchAction: 'none' }}
      onPointerDown={stopPropagationIfOverlaySurface}
      onPointerMove={stopPropagationIfOverlaySurface}
      onWheel={stopPropagationIfOverlaySurface}
      onTouchMove={stopPropagationIfOverlaySurface}
      onClick={stopPropagationIfOverlaySurface}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={popupRef}
        className={`flex flex-col rounded-lg bg-white p-6 shadow-lg ${additionalClass}`}
        style={{
          width, maxWidth, height, maxHeight,
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-bold text-black">{title}</h2>
          {!disableCloseButton && (
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={onClose}
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          )}
        </div>
        <div className="relative mt-4 flex grow flex-col overflow-y-auto">
          {message ? (
            <p className="mb-4 text-gray-600">{message}</p>
          ) : (
            children
          )}
        </div>
        {(onConfirm || onCancel) && (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
            {onCancel && (
              <button
                type="button"
                className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  onCancel?.();
                  onClose?.();
                }}
                disabled={loading}
              >
                {cancelLabel}
              </button>
            )}
            {onConfirm && (
              <button
                type="button"
                className="rounded bg-rgpd-blue-light px-4 py-2 text-white hover:brightness-110 disabled:opacity-60"
                onClick={onConfirm}
                disabled={loading}
              >
                {loading ? 'Veuillez patienter…' : confirmLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render in a portal to escape any parent stacking context
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body);
  }
  return modal;
}

export default Popup;
